'use client'

import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { useEffect, useRef } from 'react'
import { FB_PIXEL_ENABLED, FB_PIXEL_NOSCRIPT_SRC, fbPageView, fbPixelSnippet } from '@/lib/fbq'

// Meta Pixel, mounted ONLY in the (marketing) layout.
//
// That placement is the privacy control, not a preference. The Pixel reports
// the page URL at send time and has no supported client-side override, so the
// only way to keep tenant identifiers away from Meta is to keep the tag off the
// pages whose URLs carry them. See lib/fbq.ts for the routes and what each one
// carries.
//
// This layout renders for exactly two URLs, both clean:
//   /         proxy.ts rewrites a LOGGED-OUT root here (a signed-in root renders
//             the (app) group instead, so the Pixel never loads for a customer)
//   /thanks   the demo-request confirmation
//
// BOOTSTRAP (ENG-4154). The first revision pointed `next/script` at
// fbevents.js and skipped Meta's stub, on the reasoning that `onReady` fires
// after `window.fbq` exists. It does not: fbevents.js ATTACHES to an `fbq` the
// stub must already have created, so loaded alone it defined nothing and every
// call no-op'd against `undefined` — silently, with a 200 and no console error.
// The stub and the loader are now one inline script, Meta's own, so their order
// cannot be got wrong. Everything below assumes `window.fbq` exists because
// that script created it.
export function MetaPixel() {
  const pathname = usePathname()
  // The snippet fires the ENTRY PageView itself. Without this guard the effect
  // would fire a second one for the same page on mount, double-counting every
  // session's first view.
  const entryCounted = useRef(false)

  useEffect(() => {
    if (!FB_PIXEL_ENABLED) return
    if (!entryCounted.current) {
      entryCounted.current = true
      return
    }
    // Client-side navigation. The snippet's PageView fires once and never
    // again, so on a SPA this is what counts every page after the first.
    fbPageView()
  }, [pathname])

  if (!FB_PIXEL_ENABLED) return null

  return (
    <>
      <Script
        id="meta-pixel"
        strategy="afterInteractive"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: fbPixelSnippet() }}
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img height="1" width="1" style={{ display: 'none' }} src={FB_PIXEL_NOSCRIPT_SRC} alt="" />
      </noscript>
    </>
  )
}
